import { createSlice } from "@reduxjs/toolkit";
import {
  createTicketAction,
  updateTicketAction,
  getTicketsAction,
  deleteTicketAction,
  deleteTicketDocumentAction,
} from "../../actions/Tickets/ticketsAction";

const initialState = {
  tickets: [],
  loading: false,

  // Create flags
  createLoading: false,
  createSuccess: false,
  createError: null,

  // Update
  updateLoading: false,
  updateSuccess: false,
  updateError: null,

  // Delete Ticket
  deleteLoading: false,
  deleteSuccess: false,
  deleteError: null,

  // Delete Document
  deleteDocLoading: false,
  deleteDocSuccess: false,
  deleteDocError: null,

  hasFetched: false,
};

const ticketsSlice = createSlice({
  name: "tickets",
  initialState,
  reducers: {
    resetTicketFlags: (state) => {
      state.createSuccess = false;
      state.createError = null;
      state.updateSuccess = false;
      state.updateError = null;
      state.deleteSuccess = false;
      state.deleteError = null;
      state.deleteDocSuccess = false;
      state.deleteDocError = null;
    },

    clearTickets: (state) => {
      state.tickets = [];
      state.loading = true;
    },

    updateLocalTickets: (state, action) => {
      const tickets = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];

      tickets.forEach((ticket) => {
        if (ticket && typeof ticket === "object" && ticket.TicketID) {
          const index = state.tickets.findIndex(
            (t) => t.TicketID === ticket.TicketID
          );
          if (index !== -1) {
            state.tickets[index] = ticket;
          } else {
            state.tickets.push(ticket);
          }
        }
      });
    },

    removeLocalTicket: (state, action) => {
      state.tickets = state.tickets.filter(
        (t) => t.TicketID !== action.payload
      );
    },

    setHasFetched: (state) => {
      state.hasFetched = true;
    },
  },

  extraReducers: (builder) => {
    builder
      /* =======================================
            GET TICKETS
      ======================================= */
      .addCase(getTicketsAction.pending, (state) => {
        state.loading = true;
        state.hasFetched = false;
      })
      .addCase(getTicketsAction.fulfilled, (state, action) => {
        state.loading = false;
        const tickets = action.payload || [];

        const validTickets = tickets.filter(
          (t) => t && typeof t === "object" && t.TicketID
        );

        state.tickets = validTickets;
        state.hasFetched = true;
      })
      .addCase(getTicketsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.hasFetched = true;
      })

      /* =======================================
            CREATE TICKET
      ======================================= */
      .addCase(createTicketAction.pending, (state) => {
        state.createLoading = true;
        state.createSuccess = false;
        state.createError = null;
      })
      .addCase(createTicketAction.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;

        const newTicket = action.payload?.ticket || null;
        if (newTicket && newTicket.TicketID) {
          state.tickets.push(newTicket);
        }

        state.hasFetched = false;
      })
      .addCase(createTicketAction.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload;
      })

      /* =======================================
            UPDATE TICKET
      ======================================= */
      .addCase(updateTicketAction.pending, (state) => {
        state.updateLoading = true;
        state.updateSuccess = false;
        state.updateError = null;
      })
      .addCase(updateTicketAction.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = true;

        const updatedTicket = action.payload?.ticket;

        if (updatedTicket && updatedTicket.TicketID) {
          const index = state.tickets.findIndex(
            (t) => t.TicketID === updatedTicket.TicketID
          );
          if (index !== -1) {
            state.tickets[index] = updatedTicket;
          }
        }

        state.hasFetched = false;
      })
      .addCase(updateTicketAction.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
      })

      /* =======================================
            DELETE TICKET
      ======================================= */
      .addCase(deleteTicketAction.pending, (state) => {
        state.deleteLoading = true;
        state.deleteSuccess = false;
        state.deleteError = null;
      })
      .addCase(deleteTicketAction.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = true;

        state.tickets = state.tickets.filter(
          (t) => t.TicketID !== action.payload
        );
      })
      .addCase(deleteTicketAction.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
      })

      /* =======================================
            DELETE DOCUMENT
      ======================================= */
      .addCase(deleteTicketDocumentAction.pending, (state) => {
        state.deleteDocLoading = true;
        state.deleteDocSuccess = false;
        state.deleteDocError = null;
      })
      .addCase(deleteTicketDocumentAction.fulfilled, (state) => {
        state.deleteDocLoading = false;
        state.deleteDocSuccess = true;
      })
      .addCase(deleteTicketDocumentAction.rejected, (state, action) => {
        state.deleteDocLoading = false;
        state.deleteDocError = action.payload;
      });
  },
});

export const {
  clearTickets,
  resetTicketFlags,
  updateLocalTickets,
  removeLocalTicket,
  setHasFetched,
} = ticketsSlice.actions;

export default ticketsSlice.reducer;
